import { useTranslation } from 'react-i18next';
import { Button, Callout, Modal } from '@/components/ui';
import type { HierarchyPageVM } from './useHierarchyPage';
import styles from './OrgMoveConfirmDialog.module.css';

export interface OrgMoveConfirmDialogProps {
    vm: HierarchyPageVM;
}

/**
 * EAA-1818: frame 29's `Chuyển "X" vào "Y"?` confirmation, raised by BOTH move
 * entry points — a completed drag and the "Chuyển đến…" picker — so a re-parent
 * is never a single unrecoverable gesture.
 *
 * The amber panel is fixed design copy and it states a real limitation, not a
 * placeholder: there is no `move-preview` endpoint (explicitly out of scope in
 * EAA-1836), and there could not usefully be a simple one, because app scope is
 * stored per organization and is not inherited down the tree. So the honest
 * message is exactly the design's — the system cannot precompute a permission
 * delta, check the destination's own scope yourself.
 *
 * The body copy says what the move DOES do and, just as importantly, what it does
 * not: Levels and roles are untouched, and no account gains or loses an
 * application.
 */
export function OrgMoveConfirmDialog({ vm }: OrgMoveConfirmDialogProps) {
    const { t } = useTranslation();
    const {
        pendingMove,
        pendingAccounts,
        cancelPendingMove,
        confirmPendingMove,
        movePending,
        knownOrgs,
    } = vm;

    const sourceName = pendingMove?.node.organization.name ?? '';
    const currentParent = pendingMove?.previousParentId
        ? (knownOrgs.get(pendingMove.previousParentId)?.name ??
          t('console.hierarchy.moveConfirmDialog.fallbackParent'))
        : t('console.hierarchy.moveConfirmDialog.rootFallback');

    const accountsClause =
        pendingAccounts === null
            ? ''
            : t('console.hierarchy.moveConfirmDialog.accountsClause', {
                  value: pendingAccounts.toLocaleString('vi-VN'),
              });

    return (
        <Modal
            open={Boolean(pendingMove)}
            onCancel={cancelPendingMove}
            title={
                pendingMove
                    ? t('console.hierarchy.moveConfirmDialog.title', {
                          source: sourceName,
                          target: pendingMove.targetName,
                      })
                    : ''
            }
            // EAA-1804: kit `Button` instead of hand-written `<button className="portal-btn">`
            // — see AccountDetailModal.tsx for the mapping rationale.
            footer={
                <>
                    <Button onClick={cancelPendingMove} disabled={movePending}>
                        {t('common.cancel')}
                    </Button>
                    <Button type="primary" onClick={confirmPendingMove} disabled={movePending}>
                        {movePending
                            ? t('console.hierarchy.moveConfirmDialog.movingLabel')
                            : t('console.hierarchy.moveConfirmDialog.confirmButton')}
                    </Button>
                </>
            }
        >
            <div className={styles.path}>
                <span className={styles.pathFrom}>{currentParent}</span>
                <svg
                    viewBox="0 0 24 24"
                    width="15"
                    height="15"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                >
                    <path d="M5 12h14M13 6l6 6-6 6" />
                </svg>
                <span className={styles.pathTo}>{pendingMove?.targetName}</span>
            </div>

            <p className="portal-confirm-message">
                {t('console.hierarchy.moveConfirmDialog.message', { accountsClause })}
            </p>

            <Callout
                tone="warning"
                label={t('console.hierarchy.moveConfirmDialog.previewUnavailableLabel')}
                longLabel
            >
                {t('console.hierarchy.moveConfirmDialog.previewUnavailableBody')}
            </Callout>
        </Modal>
    );
}

export default OrgMoveConfirmDialog;
